import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { flushSync } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import api, { API_BASE_URL } from "./lib/api";
import Navbar from "@/components/Navbar";
import ProtectedRoute from "@/components/ProtectedRoute";

const HomePage = lazy(() => import("@/pages/HomePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const ImageBuilderPage = lazy(() => import("@/pages/ImageBuilderPage"));
const AudioGenPage = lazy(() => import("@/pages/AudioGenPage"));
const PhotoUploadPage = lazy(() => import("@/pages/PhotoUploadPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ListingBuilderPage = lazy(() => import("@/pages/ListingBuilderPage"));
const ListingBuilderDraftPage = lazy(() => import("@/pages/ListingBuilderDraftPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const AdminJobsPage = lazy(() => import("@/pages/AdminJobsPage"));
const AdminFlowerPage = lazy(() => import("@/pages/AdminFlowerPage"));
const PhotoShootsPage = lazy(() => import("@/pages/PhotoShootsPage"));
const SparkCreatorLayout = lazy(() => import("@/pages/spark_creator/SparkCreatorLayout"));
const SCManageAssetsPage = lazy(() => import("@/pages/spark_creator/ManageAssetsPage"));
const SCGenerateHubPage = lazy(() => import("@/pages/spark_creator/GenerateHubPage"));
const SCGenerateEmbedPage = lazy(() => import("@/pages/spark_creator/GenerateEmbedPage"));
const SCEditPage = lazy(() => import("@/pages/spark_creator/EditPage"));
const SCToolsImageHostPage = lazy(() => import("@/pages/spark_creator/ToolsImageHostPage"));
const SCToolsImageEditorPage = lazy(() => import("@/pages/spark_creator/ToolsImageEditorPage"));
const SCToolsUpscalerPage = lazy(() => import("@/pages/spark_creator/ToolsUpscalerPage"));
const SCFoldersPage = lazy(() => import("@/pages/spark_creator/FoldersPage"));
const SCProjectsHomePage = lazy(() => import("@/pages/spark_creator/ProjectsHomePage"));
const SCProjectWorkspacePage = lazy(() => import("@/pages/spark_creator/ProjectWorkspacePage"));
const SCImageStudioSection = lazy(() => import("@/pages/spark_creator/ImageStudioSection"));
const SCVideoStudioSection = lazy(() => import("@/pages/spark_creator/VideoStudioSection"));
const SCToolsHubPage = lazy(() => import("@/pages/spark_creator/ToolsHubPage"));
const SCToolsAudioPage = lazy(() => import("@/pages/spark_creator/ToolsAudioPage"));
const SCListingInShellPage = lazy(() => import("@/pages/spark_creator/ListingInShellPage"));
const SCToolsCharacterPage = lazy(() => import("@/pages/spark_creator/ToolsCharacterPage"));
const SCCreditsSection = lazy(() => import("@/pages/spark_creator/CreditsSection"));
const SCSettingsSection = lazy(() => import("@/pages/spark_creator/SettingsSection"));
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import logoImage from "@/assets/logo.png";
import { PollingProvider } from "@/contexts/PollingContext";
import { TimezoneProvider, useUpdateTimezone } from "@/contexts/TimezoneContext";
import { useTheme, resolveThemePreference } from "@/contexts/ThemeContext";

function LegacyRedirect({ from, to }) {
  const location = useLocation();
  const tail = location.pathname.startsWith(from)
    ? location.pathname.slice(from.length)
    : "";
  return <Navigate to={`${to}${tail}${location.search}${location.hash}`} replace />;
}

function SearchRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function NavbarShell({ isAuthenticated, user, onLogout }) {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, "") || "/";
  if (pathname === "/spark-creator" || pathname.startsWith("/spark-creator/")) return null;
  return <Navbar isAuthenticated={isAuthenticated} user={user} onLogout={onLogout} />;
}

function TimezoneSync({ timezone }) {
  const update = useUpdateTimezone();
  useEffect(() => {
    update(timezone || null);
  }, [timezone, update]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const { theme, setTheme } = useTheme();
  const themeReadyToSync = useRef(false);

  useEffect(() => {
    const validateAuth = async () => {
      try {
        const response = await api.get(`/auth/session`);

        if (response.data.authenticated) {
          const userData = response.data.user;
          const resolvedTheme = resolveThemePreference(userData.theme_preference);
          if (resolvedTheme) {
            setTheme(resolvedTheme);
          }
          themeReadyToSync.current = true;
          setUser(userData);
          setIsAuthenticated(true);
          setAuthLoading(false);
          return;
        }
      } catch (error) {
        console.log('No cookie session found');
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('walmart_callback_uri');
      localStorage.removeItem('walmart_pending_oauth');
      localStorage.removeItem('walmart_connect_mode');

      setAuthLoading(false);
    };

    validateAuth();
  }, []);

  useEffect(() => {
    if (!themeReadyToSync.current || !isAuthenticated) return;
    const timer = setTimeout(() => {
      api.patch(`/auth/preferences`, { theme_preference: theme })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(timer);
  }, [theme]);

  const handleLogin = (userData) => {
    const userObj = userData?.user || userData;
    const resolvedTheme = resolveThemePreference(userObj?.theme_preference);
    if (resolvedTheme) {
      setTheme(resolvedTheme);
    }
    themeReadyToSync.current = true;
    flushSync(() => {
      setUser(userObj);
      setIsAuthenticated(true);
    });
  };

  const refreshUser = async () => {
    try {
      const response = await api.get(`/auth/session`);
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
      }
    } catch (e) {
      console.warn('Failed to refresh user session:', e);
    }
  };

  const handleLogout = async () => {
    setUser(null);
    setIsAuthenticated(false);
    toast.success("Logging out...");
    try {
      const { signOutFirebase } = await import('./firebase');
      await signOutFirebase();
    } catch (e) {}
    window.location.href = `${API_BASE_URL}/auth/logout`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <img src={logoImage} alt="SparkSeller" className="w-16 h-16 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <TimezoneProvider>
    <PollingProvider>
    <TimezoneSync timezone={user?.timezone} />
    <div className="App">
      <Toaster position="top-right" richColors />

      <BrowserRouter>
        <NavbarShell isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
        <Suspense fallback={
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <img src={logoImage} alt="SparkSeller" className="w-12 h-12 animate-pulse" />
          </div>
        }>
        <Routes>
          <Route path="/" element={isAuthenticated ? <Navigate to="/spark-creator" replace /> : <HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/spark-creator" replace /> : <LoginPage onLogin={handleLogin} />} />
          <Route path="/signup" element={isAuthenticated ? <Navigate to="/spark-creator" replace /> : <SignupPage onLogin={handleLogin} />} />
          <Route path="/auth-callback" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          <Route path="/dashboard" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/items" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/walmart/*" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/wfs-estimator" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/connect/walmart" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/spark-connect" element={<Navigate to="/billing" replace />} />
          <Route path="/affiliates" element={<Navigate to="/" replace />} />
          <Route path="/affiliate-dashboard" element={<Navigate to="/spark-creator" replace />} />
          <Route path="/admin/connect" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/stores" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/stress-test" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/affiliates" element={<Navigate to="/admin" replace />} />

          <Route path="/settings" element={<LegacyRedirect from="/settings" to="/spark-creator/settings" />} />
          <Route
            path="/image-builder"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_tools">
                <ImageBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route path="/cinema-studio" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route path="/cinema-studio/projects/:projectId" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route path="/video-builder" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route path="/video-builder/*" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route
            path="/audio-gen"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_tools">
                <AudioGenPage />
              </ProtectedRoute>
            }
          />
          <Route path="/ad-wizard" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route
            path="/photo-upload"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_tools">
                <PhotoUploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/users" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/email-settings" element={<Navigate to="/admin" replace />} />
          <Route
            path="/admin/jobs"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminJobsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/flower"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminFlowerPage />
              </ProtectedRoute>
            }
          />
          <Route path="/character-creator" element={<LegacyRedirect from="/character-creator" to="/spark-creator/tools/character" />} />
          <Route path="/seedance-studio" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route path="/seedance-studio/*" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route
            path="/photo-shoots"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_tools">
                <PhotoShootsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/mimic-wizard" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route path="/ugc-creator" element={<SearchRedirect to="/spark-creator/video" />} />
          <Route path="/listing-builder" element={<Navigate to="/spark-creator/tools/listing" replace />} />
          <Route path="/listing-builder/:draftId" element={<LegacyRedirect from="/listing-builder" to="/spark-creator/tools/listing" />} />
          <Route
            path="/billing"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/credits" element={<LegacyRedirect from="/credits" to="/spark-creator/credits" />} />

          <Route
            path="/spark-creator"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_tools">
                <SparkCreatorLayout user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          >
            <Route index element={<SCProjectsHomePage />} />
            <Route path="projects/:projectId" element={<SCProjectWorkspacePage />} />
            <Route path="image" element={<SCImageStudioSection />} />
            <Route path="image/product" element={<SCImageStudioSection initialMode="product" />} />
            <Route path="image/character" element={<SCImageStudioSection initialMode="character" />} />
            <Route path="image/photo-shoot" element={<SCImageStudioSection initialMode="photo-shoot" />} />
            <Route path="image/editor" element={<SCImageStudioSection initialMode="editor" />} />
            <Route path="video" element={<SCVideoStudioSection initialMode="generate" />} />
            <Route path="video/templates" element={<SCVideoStudioSection initialMode="templates" />} />
            <Route path="video/generate" element={<SCVideoStudioSection initialMode="generate" />} />
            <Route path="video/clip" element={<SCVideoStudioSection initialMode="generate" />} />
            <Route path="video/cinema-editor" element={<SCVideoStudioSection initialMode="cinema-editor" />} />
            <Route path="video/music" element={<Navigate to="/spark-creator/tools/music" replace />} />
            <Route path="video/narration" element={<Navigate to="/spark-creator/tools/narration" replace />} />
            <Route path="credits" element={<SCCreditsSection />} />
            <Route path="settings" element={<SCSettingsSection onUserRefresh={refreshUser} />} />
            <Route path="tools" element={<SCToolsHubPage />} />
            <Route path="tools/listing" element={<SCListingInShellPage />} />
            <Route path="tools/listing/:draftId" element={<SCListingInShellPage />} />
            <Route path="tools/character" element={<SCToolsCharacterPage />} />
            <Route path="manage" element={<Navigate to="/spark-creator" replace />} />
            <Route path="manage/images" element={<Navigate to="/spark-creator/image" replace />} />
            <Route path="manage/videos" element={<Navigate to="/spark-creator/video" replace />} />
            <Route path="manage/audio" element={<Navigate to="/spark-creator/tools/music" replace />} />
            <Route path="manage/photo-shoots" element={<Navigate to="/spark-creator/image/photo-shoot" replace />} />
            <Route path="manage/music" element={<Navigate to="/spark-creator/tools/music" replace />} />
            <Route path="manage/narration" element={<Navigate to="/spark-creator/tools/narration" replace />} />
            <Route path="manage/folders" element={<SCFoldersPage />} />
            <Route path="generate" element={<Navigate to="/spark-creator/image" replace />} />
            <Route path="generate/image-studio" element={<Navigate to="/spark-creator/image" replace />} />
            <Route path="generate/image" element={<Navigate to="/spark-creator/image/product" replace />} />
            <Route path="generate/character" element={<Navigate to="/spark-creator/tools/character" replace />} />
            <Route path="generate/photo-shoot" element={<Navigate to="/spark-creator/image/photo-shoot" replace />} />
            <Route path="generate/video" element={<Navigate to="/spark-creator/video/generate" replace />} />
            <Route path="generate/music" element={<Navigate to="/spark-creator/tools/music" replace />} />
            <Route path="generate/narration" element={<Navigate to="/spark-creator/tools/narration" replace />} />
            <Route path="generate/:slug" element={<SCGenerateEmbedPage />} />
            <Route path="edit" element={<SCEditPage />} />
            <Route path="tools/image-editor" element={<SCToolsImageEditorPage />} />
            <Route path="tools/upscaler" element={<SCToolsUpscalerPage />} />
            <Route path="tools/image-host" element={<SCToolsImageHostPage />} />
            <Route path="tools/music" element={<SCToolsAudioPage initialTab="music" />} />
            <Route path="tools/narration" element={<SCToolsAudioPage initialTab="narration" />} />
            <Route path="tools/cinema" element={<Navigate to="/spark-creator/edit" replace />} />
            <Route path="archive" element={<SCManageAssetsPage archivedOnly />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
    </PollingProvider>
    </TimezoneProvider>
  );
}

export default App;
